import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { PiggyBank, Wind, Coins, ArrowRight } from "lucide-react";

interface Point {
  x: number;
  y: number;
}

export const SavingsTossGame = () => {
  // Game state
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [windForce, setWindForce] = useState(0);
  const [coinPosition, setCoinPosition] = useState<Point>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Point | null>(null);
  
  // Refs for canvas and animation
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  
  // Constants
  const CANVAS_WIDTH = 320;
  const CANVAS_HEIGHT = 480;
  const COIN_SIZE = 20;
  const PIGGY_SIZE = 60;
  const MAX_WIND = 5;
  const GRAVITY = 0.5;
  
  // Initialize game
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    // Set initial coin position
    setCoinPosition({
      x: CANVAS_WIDTH / 2,
      y: CANVAS_HEIGHT - COIN_SIZE * 2
    });
    
    // Start wind changes
    const windInterval = setInterval(() => {
      setWindForce(Math.random() * MAX_WIND * 2 - MAX_WIND);
    }, 3000);
    
    return () => {
      clearInterval(windInterval);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);
  
  // Handle touch/mouse events
  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    const point = getEventPoint(e);
    setIsDragging(true);
    setDragStart(point);
  };
  
  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || !dragStart) return;
    const point = getEventPoint(e);
    setCoinPosition(prev => ({
      x: prev.x + (point.x - dragStart.x) * 0.1,
      y: prev.y + (point.y - dragStart.y) * 0.1
    }));
  };
  
  const handleEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || !dragStart) return;
    const point = getEventPoint(e);
    const force = {
      x: (dragStart.x - point.x) * 0.2,
      y: (dragStart.y - point.y) * 0.2
    };
    throwCoin(force);
    setIsDragging(false);
    setDragStart(null);
  };
  
  // Helper to get event coordinates
  const getEventPoint = (e: React.MouseEvent | React.TouchEvent): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };
  
  // Coin throwing animation
  const throwCoin = (force: Point) => {
    let velocity = { x: force.x, y: force.y };
    let position = { ...coinPosition };
    
    const animate = () => {
      // Apply wind and gravity
      velocity.x += windForce * 0.1;
      velocity.y += GRAVITY;
      
      // Update position
      position.x += velocity.x;
      position.y += velocity.y;
      
      // Check for piggy bank collision
      const piggyPosition = {
        x: CANVAS_WIDTH / 2,
        y: PIGGY_SIZE * 1.5
      };
      
      if (
        Math.abs(position.x - piggyPosition.x) < PIGGY_SIZE / 2 &&
        Math.abs(position.y - piggyPosition.y) < PIGGY_SIZE / 2
      ) {
        // Successful save!
        setScore(prev => prev + 1);
        toast.success("Great save! 🎯");
        resetCoin();
        return;
      }
      
      // Check for out of bounds
      if (
        position.x < 0 ||
        position.x > CANVAS_WIDTH ||
        position.y < 0 ||
        position.y > CANVAS_HEIGHT
      ) {
        toast.error("Missed! Try again!");
        resetCoin();
        return;
      }
      
      // Update coin position
      setCoinPosition(position);
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animate();
  };
  
  // Reset coin to starting position
  const resetCoin = () => {
    setCoinPosition({
      x: CANVAS_WIDTH / 2,
      y: CANVAS_HEIGHT - COIN_SIZE * 2
    });
  };
  
  // Render game
  const renderGame = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // Draw wind indicator
    ctx.fillStyle = "#94a3b8";
    ctx.font = "16px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(`Wind: ${windForce > 0 ? "→" : "←"} ${Math.abs(windForce).toFixed(1)}`, CANVAS_WIDTH / 2, 30);
    
    // Draw piggy bank
    ctx.fillStyle = "#ec4899";
    ctx.beginPath();
    ctx.arc(CANVAS_WIDTH / 2, PIGGY_SIZE * 1.5, PIGGY_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw coin
    ctx.fillStyle = "#fbbf24";
    ctx.beginPath();
    ctx.arc(coinPosition.x, coinPosition.y, COIN_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
  };
  
  // Update canvas on state changes
  useEffect(() => {
    renderGame();
  }, [coinPosition, windForce]);
  
  return (
    <Card className="p-4 max-w-md mx-auto">
      <div className="text-center mb-4">
        <h2 className="text-2xl font-bold mb-2">Savings Toss</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Flick coins into the piggy bank! Watch out for the wind!
        </p>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-yellow-500" />
            <span className="font-bold">Score: {score}</span>
          </div>
          <div className="flex items-center gap-2">
            <Wind className="w-5 h-5 text-blue-500" />
            <span>Wind: {Math.abs(windForce).toFixed(1)}</span>
          </div>
        </div>
      </div>
      
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="border border-border rounded-lg mx-auto touch-none"
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />
        
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg">
            <Button 
              size="lg"
              onClick={() => setIsPlaying(true)}
              className="animate-bounce"
            >
              Start Game
            </Button>
          </div>
        )}
      </div>
      
      <div className="mt-4 text-center text-sm text-muted-foreground">
        <p className="md:hidden">Drag and release to toss coins!</p>
        <p className="hidden md:block">Click and drag to toss coins!</p>
        <p>Adjust for the wind to make successful saves!</p>
      </div>
    </Card>
  );
};